export const CONTENT_FORMAT = "blocks";

export const BLOCK_TYPES = [
  { tag: "h2", label: "Heading", size: 14, bold: true },
  { tag: "h3", label: "Sub-heading", size: 12, bold: true },
  { tag: "p", label: "Paragraph", size: 0, bold: false },
];

export const DEFAULT_BLOCK_TAG = "p";

export const blockType = (tag) =>
  BLOCK_TYPES.find((item) => item.tag === tag) ||
  BLOCK_TYPES.find((item) => item.tag === DEFAULT_BLOCK_TAG);

export const FONT_SIZES = [9, 10, 11, 12, 13, 14, 16, 18, 20, 24];

export const TEXT_ALIGNMENTS = ["left", "center", "right", "justify"];

export const LIST_STYLES = ["ul", "ol"];

const MARKS = ["bold", "italic", "underline"];

const asArray = (value) => (Array.isArray(value) ? value : []);

const text = (value) => (typeof value === "string" ? value : "");

export const normalizeRun = (run) => {
  const normalized = { text: text(run?.text) };
  for (const mark of MARKS) {
    if (run?.[mark] === true) normalized[mark] = true;
  }
  return normalized;
};

const sameMarks = (a, b) => MARKS.every((mark) => Boolean(a[mark]) === Boolean(b[mark]));

export const mergeRuns = (runs) =>
  asArray(runs)
    .map(normalizeRun)
    .reduce((merged, run) => {
      const last = merged[merged.length - 1];
      if (last && sameMarks(last, run)) {
        last.text += run.text;
        return merged;
      }
      if (run.text === "" && merged.length) return merged;
      merged.push(run);
      return merged;
    }, [])
    .filter((run, index, all) => run.text !== "" || all.length === 1);

export const normalizeBlock = (block) => {
  const tag = blockType(block?.tag).tag;
  const list = LIST_STYLES.includes(block?.list) ? block.list : "";
  const align = TEXT_ALIGNMENTS.includes(block?.align) ? block.align : "";
  const size = Number(block?.size);

  return {
    tag,
    ...(list ? { list } : {}),
    ...(align ? { align } : {}),
    ...(Number.isFinite(size) && size > 0 ? { size } : {}),
    runs: mergeRuns(block?.runs).filter((run) => run.text !== ""),
  };
};

export const normalizeBlocks = (value) => asArray(value).map(normalizeBlock);

export const blockText = (block) =>
  mergeRuns(block?.runs)
    .map((run) => run.text)
    .join("");

export const blocksToPlainText = (blocks) =>
  normalizeBlocks(blocks)
    .map(blockText)
    .join("\n");

/** Nothing worth printing — used by the "add some content first" guards. */
export const blocksAreEmpty = (blocks) =>
  blocksToPlainText(blocks).trim() === "";

export const groupBlocks = (blocks) => {
  const groups = [];

  for (const block of normalizeBlocks(blocks)) {
    const last = groups[groups.length - 1];

    if (block.list) {
      if (last?.kind === "list" && last.list === block.list) {
        last.items.push(block);
      } else {
        groups.push({ kind: "list", list: block.list, items: [block] });
      }
      continue;
    }
    groups.push({ kind: "block", block });
  }

  return groups;
};

export const runStyle = (run) => ({
  ...(run?.bold ? { fontWeight: "bold" } : {}),
  ...(run?.italic ? { fontStyle: "italic" } : {}),
  ...(run?.underline ? { textDecoration: "underline" } : {}),
});

export const blockStyle = (block) => {
  const def = blockType(block?.tag);
  const size = Number(block?.size) > 0 ? Number(block.size) : def.size;

  return {
    ...(block?.align ? { textAlign: block.align } : {}),
    ...(size ? { fontSize: `${size}pt` } : {}),
    ...(def.bold ? { fontWeight: "bold" } : {}),
  };
};


export const isContentElement = (element) =>
  element?.type === "richText" && element?.props?.format === CONTENT_FORMAT;

export const blocksOf = (element) =>
  isContentElement(element) ? normalizeBlocks(element.props?.blocks) : [];

/** Element types whose text the one content editor replaces. */
export const TEXT_ELEMENT_TYPES = ["heading", "subHeading", "paragraph", "richText"];

export const isTextElement = (element) =>
  TEXT_ELEMENT_TYPES.includes(element?.type);


const LEGACY_TAGS = {
  heading: "h2",
  subHeading: "h3",
  paragraph: "p",
  richText: "p",
};

const localizedText = (value, lang = "en") => {
  if (value == null) return "";
  if (typeof value === "string") return value;

  const own = value[lang];
  if (typeof own === "string" && own.trim()) return own;
  for (const code of ["en", "hi"]) {
    const other = value[code];
    if (typeof other === "string" && other.trim()) return other;
  }
  return "";
};

const blocksFromText = (value, { tag, list = "", style = {} } = {}) => {
  const marks = {
    ...(style.fontWeight === "bold" ? { bold: true } : {}),
    ...(style.fontStyle === "italic" ? { italic: true } : {}),
  };
  const size = Number(style.fontSize);
  const align = TEXT_ALIGNMENTS.includes(style.align) ? style.align : "";

  return String(value ?? "")
    .split("\n")
    .map((line) =>
      normalizeBlock({
        tag,
        list,
        align,
        size: Number.isFinite(size) ? size : 0,
        runs: [{ text: line, ...marks }],
      })
    );
};

export const blocksFromElements = (elements, lang = "en") =>
  asArray(elements).flatMap((element) => {
    if (isContentElement(element)) return blocksOf(element);
    if (!isTextElement(element)) return [];

    return blocksFromText(localizedText(element.content, lang), {
      tag: LEGACY_TAGS[element.type] || DEFAULT_BLOCK_TAG,
      list: element.props?.listStyle === "bullet" ? "ul" : "",
      style: element.style || {},
    });
  });


const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const styleAttribute = (block) => {
  const parts = [
    block.align ? `text-align:${block.align}` : "",
    block.size ? `font-size:${block.size}pt` : "",
  ].filter(Boolean);

  return parts.length ? ` style="${parts.join(";")}"` : "";
};

const runHtml = (run) => {
  let html = escapeHtml(run.text) || "<br>";
  if (run.bold) html = `<b>${html}</b>`;
  if (run.italic) html = `<i>${html}</i>`;
  if (run.underline) html = `<u>${html}</u>`;
  return html;
};

const blockInnerHtml = (block) =>
  block.runs.length ? block.runs.map(runHtml).join("") : "<br>";

export const blocksToHtml = (blocks) =>
  groupBlocks(blocks)
    .map((group) => {
      if (group.kind === "list") {
        const items = group.items
          .map(
            (block) =>
              `<li${styleAttribute(block)}>${blockInnerHtml(block)}</li>`
          )
          .join("");
        return `<${group.list}>${items}</${group.list}>`;
      }

      const { block } = group;
      return `<${block.tag}${styleAttribute(block)}>${blockInnerHtml(
        block
      )}</${block.tag}>`;
    })
    .join("");

const HTML_TAGS = {
  H1: "h2",
  H2: "h2",
  H3: "h3",
  H4: "h3",
  P: "p",
  DIV: "p",
  LI: "p",
};

const BLOCK_LEVEL = new Set([...Object.keys(HTML_TAGS), "UL", "OL", "BLOCKQUOTE"]);

/** `13pt` / `17px` → points. The document is laid out in points. */
const pointSize = (value) => {
  const match = /^([\d.]+)(pt|px)?$/.exec(String(value || "").trim());
  if (!match) return 0;

  const size = Number(match[1]);
  if (!Number.isFinite(size) || size <= 0) return 0;
  return Math.round(match[2] === "px" ? size * 0.75 : size);
};

const markOf = (node) => {
  const tag = node.nodeName;
  const style = node.style || {};
  const weight = String(style.fontWeight || "");

  return {
    ...(tag === "B" || tag === "STRONG" || weight === "bold" || Number(weight) >= 600
      ? { bold: true }
      : {}),
    ...(tag === "I" || tag === "EM" || style.fontStyle === "italic"
      ? { italic: true }
      : {}),
    ...(tag === "U" || String(style.textDecoration || "").includes("underline")
      ? { underline: true }
      : {}),
  };
};

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEMPLATE", "IFRAME", "OBJECT"]);

const collectRuns = (node, marks, runs) => {
  for (const child of Array.from(node.childNodes || [])) {
    if (child.nodeType === 3) {
      runs.push({ text: child.nodeValue || "", ...marks });
      continue;
    }
    if (child.nodeType !== 1) continue;
    if (SKIP_TAGS.has(child.nodeName)) continue;
    if (child.nodeName === "BR") {
      runs.push({ text: "\n", ...marks });
      continue;
    }
    collectRuns(child, { ...marks, ...markOf(child) }, runs);
  }
};

const hasBlockChild = (node) =>
  Array.from(node.childNodes || []).some(
    (child) => child.nodeType === 1 && BLOCK_LEVEL.has(child.nodeName)
  );

export const blocksFromHtmlNode = (root, inherited = {}, list = "") => {
  if (!root) return [];

  const blocks = [];
  const pending = [];

  const flushPending = () => {
    if (!pending.length) return;
    const runs = pending.splice(0, pending.length);
    blocks.push(...splitRunsIntoBlocks(runs, { tag: DEFAULT_BLOCK_TAG, list, ...inherited }));
  };

  for (const child of Array.from(root.childNodes || [])) {
    if (child.nodeType === 3) {
      if ((child.nodeValue || "").trim()) pending.push({ text: child.nodeValue });
      continue;
    }
    if (child.nodeType !== 1 || SKIP_TAGS.has(child.nodeName)) continue;

    if (child.nodeName === "UL" || child.nodeName === "OL") {
      flushPending();
      blocks.push(
        ...blocksFromHtmlNode(
          child,
          inherited,
          child.nodeName === "UL" ? "ul" : "ol"
        )
      );
      continue;
    }

    if (BLOCK_LEVEL.has(child.nodeName)) {
      flushPending();

      const style = child.style || {};
      const own = {
        tag: HTML_TAGS[child.nodeName] || DEFAULT_BLOCK_TAG,
        align:
          style.textAlign || child.getAttribute?.("align") || inherited.align || "",
        size: pointSize(style.fontSize) || inherited.size || 0,
      };

      if (hasBlockChild(child)) {
        blocks.push(
          ...blocksFromHtmlNode(child, { align: own.align, size: own.size }, list)
        );
        continue;
      }

      const runs = [];
      collectRuns(child, markOf(child), runs);
      blocks.push(
        ...splitRunsIntoBlocks(runs, {
          ...own,
          list: child.nodeName === "LI" ? list || "ul" : list,
        })
      );
      continue;
    }

    collectRuns(child, markOf(child), pending);
  }

  flushPending();
  return blocks;
};

/** Runs holding newlines → one block per line, keeping each line's marks. */
const splitRunsIntoBlocks = (runs, shape) => {
  const lines = [[]];

  for (const run of runs) {
    const parts = String(run.text ?? "").split("\n");
    parts.forEach((part, index) => {
      if (index > 0) lines.push([]);
      if (part !== "") lines[lines.length - 1].push({ ...run, text: part });
    });
  }

  return lines.map((line) => normalizeBlock({ ...shape, runs: line }));
};

/** Drops the blank lines a trailing `<br>` leaves behind, front and back. */
export const trimBlocks = (blocks) => {
  const normalized = normalizeBlocks(blocks);
  let start = 0;
  let end = normalized.length;

  while (start < end && blockText(normalized[start]).trim() === "") start += 1;
  while (end > start && blockText(normalized[end - 1]).trim() === "") end -= 1;

  return normalized.slice(start, end);
};
